import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ABRAlgorithm, RemotePlaybackCommand } from '../types';
import { useHls } from '../hooks/useHls';
import { useABRStats } from '../hooks/useABRStats';
import { usePlaybackResume } from '../hooks/usePlaybackResume';
import { ABRMonitor } from './ABRMonitor';
import { PlayerControls } from './PlayerControls';
import { Button } from './ui/button';
import { formatTime } from '../lib/utils';

interface VideoPlayerProps {
  src: string;
  title?: string;
  videoId?: string;
  remoteCommand?: RemotePlaybackCommand | null;
  onLocalPlay?: (currentTime: number) => void;
  onLocalPause?: (currentTime: number) => void;
  onLocalSeek?: (currentTime: number) => void;
  enablePlaybackResume?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  title,
  videoId,
  remoteCommand,
  onLocalPlay,
  onLocalPause,
  onLocalSeek,
  enablePlaybackResume = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const applyingRemoteRef = useRef(false);
  const remoteTokenRef = useRef(0);

  const [showMonitor, setShowMonitor] = useState(true);
  const [abrAlgorithm, setAbrAlgorithm] = useState<ABRAlgorithm>('ewma');

  const onAlgorithmChange = useCallback((algo: ABRAlgorithm) => {
    setAbrAlgorithm(algo);
  }, []);

  const { hls, levels, currentLevel, error } = useHls(src, videoRef, abrAlgorithm);
  const { stats, switchLog, bandwidthHistory } = useABRStats(hls, videoRef);
  const { resumedFrom, clearResumedFrom } = usePlaybackResume(
    videoRef,
    enablePlaybackResume ? videoId : null
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const emitPlay = () => {
      if (!applyingRemoteRef.current) onLocalPlay?.(video.currentTime);
    };
    const emitPause = () => {
      if (!applyingRemoteRef.current) onLocalPause?.(video.currentTime);
    };
    const emitSeek = () => {
      if (!applyingRemoteRef.current) onLocalSeek?.(video.currentTime);
    };

    video.addEventListener('play', emitPlay);
    video.addEventListener('pause', emitPause);
    video.addEventListener('seeked', emitSeek);

    return () => {
      video.removeEventListener('play', emitPlay);
      video.removeEventListener('pause', emitPause);
      video.removeEventListener('seeked', emitSeek);
    };
  }, [onLocalPlay, onLocalPause, onLocalSeek]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !remoteCommand) return;

    const token = remoteTokenRef.current + 1;
    remoteTokenRef.current = token;
    applyingRemoteRef.current = true;

    const delta = Math.abs(video.currentTime - remoteCommand.currentTime);
    if (delta > 1.5) {
      video.currentTime = remoteCommand.currentTime;
    }

    if (remoteCommand.type === 'play') {
      void video.play().catch(() => {});
    } else if (remoteCommand.type === 'pause') {
      video.pause();
    }

    window.setTimeout(() => {
      if (remoteTokenRef.current === token) {
        applyingRemoteRef.current = false;
      }
    }, 300);
  }, [remoteCommand]);

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        className="group relative aspect-video w-full select-none overflow-hidden border-2 border-foreground bg-black shadow-neo"
      >
        <video ref={videoRef} className="h-full w-full bg-black object-contain" playsInline />

        {error && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/85">
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-red-400 text-2xl font-black text-red-300">
                !
              </div>
              <p className="max-w-xs text-sm font-mono text-red-300">{error}</p>
            </div>
          </div>
        )}

        {!hls && !error && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70">
            <div className="flex flex-col items-center gap-3">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
              <p className="text-xs font-mono tracking-wider text-white/70">Initializing HLS...</p>
            </div>
          </div>
        )}

        {resumedFrom !== null && hls && !error && (
          <div className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-full border border-white/20 bg-black/75 px-3 py-1.5 text-xs font-mono text-white backdrop-blur-sm">
              <span>Resume from {formatTime(resumedFrom)}</span>
              <button
                type="button"
                className="pointer-events-auto ml-1 text-white/50 transition-colors hover:text-white"
                onClick={clearResumedFrom}
                aria-label="Hide resume notice"
                title="Hide resume notice"
              >
                x
              </button>
            </div>
          </div>
        )}

        {hls && !error && (
          <PlayerControls
            videoRef={videoRef}
            containerRef={containerRef}
            hls={hls}
            levels={levels}
            currentLevel={currentLevel}
          />
        )}
      </div>

      {title && (
        <h1 className="px-1 text-base font-black text-foreground sm:text-xl">{title}</h1>
      )}

      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowMonitor((value) => !value)}
          className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          {showMonitor
            ? <><ChevronUp className="h-3.5 w-3.5" />Hide ABR monitor</>
            : <><ChevronDown className="h-3.5 w-3.5" />Show ABR monitor</>
          }
        </Button>
      </div>

      {showMonitor && (
        <ABRMonitor
          stats={stats}
          switchLog={switchLog}
          bandwidthHistory={bandwidthHistory}
          levels={levels}
          currentLevel={currentLevel}
          abrAlgorithm={abrAlgorithm}
          onAlgorithmChange={onAlgorithmChange}
        />
      )}
    </div>
  );
};
